import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { ModalDialog, Spinner } from '@openedx/paragon';
import { getConfig } from '@edx/frontend-platform';
import { FormattedMessage } from '@edx/frontend-platform/i18n';

import { LEAD_GEN_SUBMIT_MESSAGE_KEY } from '../../constants';
import { buildLeadGenFormUrl, getLeadGenFormOrigin } from '../../utils/utmUtils';

const LeadGenModal = ({ isOpen, onClose, onSubmitted }) => {
  const { LEAD_GEN_FORM_URL: formUrl } = getConfig();
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  const formOrigin = getLeadGenFormOrigin(formUrl);
  const src = formOrigin ? buildLeadGenFormUrl(formUrl) : null;

  useEffect(() => {
    if (!isOpen || !formOrigin) {
      return undefined;
    }
    const handleMessage = (event) => {
      // Origin alone isn't enough — also require the message came from this iframe.
      if (event.origin !== formOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      const data = event.data || {};
      if (data[LEAD_GEN_SUBMIT_MESSAGE_KEY] === true) {
        onSubmitted();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, formOrigin, onSubmitted]);

  return (
    <ModalDialog
      title="Catalog download request form"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      hasCloseButton={false}
      isFullscreenOnMobile
      className="lead-gen-modal"
    >
      <ModalDialog.Body>
        {src && (
          <>
            <h2 className="h4 lead-gen-modal-title">
              <FormattedMessage
                id="leadGenModal.title"
                defaultMessage="Complete to download your custom catalog"
                description="Title shown above the lead gen form explaining what completing it unlocks."
              />
            </h2>
            {!isIframeLoaded && (
              <div className="d-flex justify-content-center align-items-center" style={{ height: 700 }}>
                <Spinner animation="border" screenReaderText="Loading form" />
              </div>
            )}
            <iframe
              ref={iframeRef}
              title="Catalog download request form"
              src={src}
              width="100%"
              height="700"
              type="text/html"
              frameBorder="0"
              scrolling="no"
              // allow-scripts/-forms/-same-origin: what Pardot needs to validate, submit,
              // and read its own tracking cookies. allow-popups: the embed's "Privacy
              // Statement" link opens in a new tab. Deliberately omits allow-top-navigation
              // so the embed can't hijack this tab.
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ border: 0, display: isIframeLoaded ? 'block' : 'none' }}
              onLoad={() => setIsIframeLoaded(true)}
            />
          </>
        )}
      </ModalDialog.Body>
    </ModalDialog>
  );
};

LeadGenModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};

export default LeadGenModal;
